/**
 * The full review sheet for one owner-submitted property, opened from the
 * approvals table on Owners & access.
 *
 * The rail on Properties takes the same decision inline, in its own column —
 * both are built from `submission-review-parts`, so they read the same evidence
 * and write the same decision.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { formatDate, formatDateTime } from "@/lib/v2-format";
import {
  KvRow,
  SubmittedUnits,
  useSubmissionDecision,
} from "./submission-review-parts";
import type { PropertySubmissionRow } from "@/hooks/v2/usePropertySubmissions";
import { useLanguage } from "@/contexts/LanguageContext";

export function PropertySubmissionReviewSheet({
  submission,
  onClose,
  canDecide = true,
}: {
  /** The row under review; `null` closes the sheet. */
  submission: PropertySubmissionRow | null;
  onClose: () => void;
  /**
   * Whether this account may approve or reject. Read-only viewers get the same
   * sheet without the decision footer — the table has always told them they see
   * this read-only, so the buttons should not be there to press.
   */
  canDecide?: boolean;
}) {
  const { t } = useLanguage();
  const { note, setNote, decide, isPending } = useSubmissionDecision(onClose);

  // A fresh note per submission. Carrying the previous one over is how a
  // rejection reason lands on the wrong building.
  useEffect(() => {
    setNote("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submission?.id]);

  const isPendingReview = submission?.approval_status === "pending";

  return (
    <Sheet open={!!submission} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        {submission && (
          <>
            <SheetHeader>
              <SheetTitle>{submission.title}</SheetTitle>
              <SheetDescription>
                {t("admin.approvals.submittedBy", "Submitted by")}{" "}
                {submission.owner_name ?? submission.owner_email ?? "—"}
                {" · "}
                {/* Time as well as date: the queue is worked by how long
                    something has been sitting, and two submissions on the same
                    day are otherwise indistinguishable. */}
                {formatDateTime(submission.submitted_at) ?? "—"}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-6 space-y-5 text-sm">
              <div>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t("admin.approvals.propertyHeading", "Property")}
                </h4>
                <div className="space-y-1.5">
                  <KvRow
                    k={t("admin.approvals.address", "Address")}
                    v={submission.address}
                  />
                  <KvRow
                    k={t("admin.approvals.cityPostal", "City / postal")}
                    v={`${submission.zip_code ? `${submission.zip_code} ` : ""}${submission.city}`}
                  />
                  <KvRow
                    k={t("admin.approvals.propertyType", "Property type")}
                    v={
                      submission.type ??
                      t("admin.approvals.notSpecified", "Not specified")
                    }
                  />
                  <KvRow
                    k={t("admin.approvals.unitsRooms", "Units & rooms")}
                    v={`${submission.apartments_count} ${t("admin.approvals.aptAbbrev", "apt")} · ${submission.rooms_count} ${t("admin.approvals.roomsWord", "rooms")}`}
                  />
                </div>
              </div>

              {/* The counts above are not enough to approve on — a decision that
                  publishes a property and its rents should be able to see those
                  rents. */}
              <SubmittedUnits propertyId={submission.id} />

              {submission.description ? (
                <div>
                  <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t("admin.approvals.description", "Description")}
                  </h4>
                  <p className="whitespace-pre-wrap rounded-lg bg-muted/50 px-3 py-2 text-muted-foreground">
                    {submission.description}
                  </p>
                </div>
              ) : null}

              {submission.approval_status === "withdrawn" && (
                <div>
                  <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t(
                      "admin.approvals.withdrawnByOwner",
                      "Withdrawn by the owner"
                    )}
                    {submission.withdrawn_at
                      ? ` · ${formatDate(submission.withdrawn_at)}`
                      : ""}
                  </h4>
                  <p className="rounded-lg bg-muted/50 px-3 py-2 text-muted-foreground">
                    {submission.withdraw_reason ??
                      t(
                        "admin.approvals.noReasonRecorded",
                        "No reason recorded."
                      )}
                  </p>
                </div>
              )}

              {/* An approved submission is a real property, but NOT a
                  published one — approval leaves is_hidden true and someone
                  publishes it deliberately. Say that, and offer the way
                  through; otherwise the sheet is a record of a building with no
                  route to the building. */}
              {submission.approval_status === "approved" && (
                <div className="flex items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2">
                  <p className="text-xs text-emerald-800">
                    {t("admin.approvals.approved", "Approved")}
                    {submission.reviewed_at
                      ? ` ${t("admin.approvals.onDatePrefix", "on")} ${formatDate(submission.reviewed_at)}`
                      : ""}
                    .{" "}
                    {t(
                      "admin.approvals.staysOffListing",
                      "It stays off the public listing until someone publishes it."
                    )}
                  </p>
                  <Button asChild size="sm" variant="outline" className="h-7">
                    <Link to={`/admin/properties/${submission.id}`}>
                      {t("admin.approvals.openProperty", "Open property")}
                    </Link>
                  </Button>
                </div>
              )}

              {submission.approval_status !== "pending" &&
                submission.review_note && (
                  <div>
                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {t("admin.approvals.previousNote", "Previous note")}
                    </h4>
                    <p className="rounded-lg bg-muted/50 px-3 py-2 text-muted-foreground">
                      {submission.review_note}
                    </p>
                  </div>
                )}

              {isPendingReview && canDecide && (
                <div>
                  <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t(
                      "admin.approvals.noteLabel",
                      "Note (optional, sent to the owner on reject)"
                    )}
                  </h4>
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={t(
                      "admin.approvals.notePlaceholder",
                      "e.g. Please add at least one photo before we can publish it."
                    )}
                    rows={3}
                  />
                </div>
              )}
            </div>

            {isPendingReview && canDecide && (
              <SheetFooter className="mt-6 flex-row justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  disabled={isPending}
                  onClick={() => decide(submission, "rejected")}
                >
                  {t("admin.approvals.reject", "Reject")}
                </Button>
                <Button
                  type="button"
                  disabled={isPending}
                  onClick={() => decide(submission, "approved")}
                >
                  {t(
                    "admin.approvals.approveKeepHidden",
                    "Approve — keep hidden"
                  )}
                </Button>
              </SheetFooter>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
